import {
  CandidateRow,
  ElementOriginMapping,
  ImportRowDraft,
  ProjectElementImport,
} from '@shared/models/element-import.model';
import { Baseline, VersionImports } from '@shared/models/project.model';
import {
  ProjectChange,
  ProjectChangeType,
} from '@shared/helpers/pipeline/update-engine.models';
import { tryMaterializeCandidateRow } from '@shared/helpers/materialize-candidate.helpers';
import { buildIfcMeasureByRowId } from '@shared/helpers/import-row-draft.helpers';
import { buildVersionImportsChange } from '@shared/helpers/candidate-discard.helpers';
import {
  ProductsById,
  RecipesById,
  collectImportedElementsInView,
} from './candidate-status.helpers';
import { MappingsByCode } from './element-imports.store';

/**
 * Other rows in the *same import* that share `row.resourceCode`, excluding the
 * row itself (DEV-3361). Materialized and discarded siblings are included: when
 * the saved mapping spreads, every such row is clobbered and recreated.
 */
export const getSameCodeSiblings = (
  row: CandidateRow,
  rowsByImport: Record<string, CandidateRow[]>,
): CandidateRow[] => {
  if (!row.resourceCode) return [];
  return (rowsByImport[row.importId] ?? []).filter(
    (candidate) =>
      candidate.id !== row.id && candidate.resourceCode === row.resourceCode,
  );
};

export type SameCodeRecreateContext = {
  version: Baseline;
  mappingsByCode: MappingsByCode;
  productsById: ProductsById;
  recipesById?: RecipesById;
  importsById: Record<string, ProjectElementImport>;
  /**
   * Per-row mapping overlay (built from the freshly refilled drafts) that drives
   * the recreate. Each affected row materializes through its own draft, so the
   * saved mapping is applied uniformly.
   */
  draftMappingsByRowId: Record<string, ElementOriginMapping | undefined>;
  /**
   * Draft to persist for every sibling (replaces any existing), or `null` to
   * clear it. Folded into the single {@link VersionImports} change so the spread
   * writes drafts + discard state atomically — drafts live on the project now,
   * so a per-sibling loop of writes would clobber itself within one batch.
   */
  siblingDraft?: ImportRowDraft | null;
};

/** Shallow equality over the flat {@link ImportRowDraft} value shape. */
const isSameDraft = (
  a: ImportRowDraft | undefined,
  b: ImportRowDraft,
): boolean => {
  if (a === undefined) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (a[key as keyof ImportRowDraft] !== b[key as keyof ImportRowDraft]) {
      return false;
    }
  }
  return true;
};

/** Apply the sibling-draft patch to a draftsByRowId map; returns whether it changed. */
const applySiblingDraftPatch = (
  draftsByRowId: Partial<Record<string, ImportRowDraft>>,
  siblings: readonly CandidateRow[],
  siblingDraft: ImportRowDraft | null | undefined,
): boolean => {
  if (siblingDraft === undefined) return false;
  let changed = false;
  for (const sibling of siblings) {
    if (siblingDraft === null) {
      if (draftsByRowId[sibling.id] === undefined) continue;
      delete draftsByRowId[sibling.id];
    } else {
      // Skip identical rewrites so an already-synced spread doesn't push an
      // empty undo frame.
      if (isSameDraft(draftsByRowId[sibling.id], siblingDraft)) continue;
      draftsByRowId[sibling.id] = siblingDraft;
    }
    changed = true;
  }
  return changed;
};

/**
 * Build the project changes that clobber and recreate every same-code sibling
 * after a mapping is saved with "also map matching rows" ticked (DEV-3361):
 *
 * 1. Remove each sibling's materialized element (always rebuilt, never kept).
 * 2. Drop each sibling's discard record (re-applied from scratch).
 * 3. Re-materialize from the refilled draft and add the new element.
 *
 * Siblings the saved mapping can't materialize (e.g. an incompatible CSV unit)
 * produce no element — they fall back to INCOMPLETE with only the product
 * pre-filled in their draft. Recreated rows materialize with `trigger: 'auto'`
 * → AUTO-MAPPED.
 */
export const buildSameCodeRecreateChanges = (
  siblings: readonly CandidateRow[],
  ctx: SameCodeRecreateContext,
): ProjectChange[] => {
  const importedElements = collectImportedElementsInView(ctx.version);
  const discardedRows = { ...(ctx.version.imports?.discardedRows ?? {}) };
  const draftsByRowId = { ...(ctx.version.imports?.draftsByRowId ?? {}) };
  const ifcMeasureByRowId = buildIfcMeasureByRowId(ctx.version, siblings);
  let discardedChanged = false;
  const changes: ProjectChange[] = [];
  const materialized: CandidateRow[] = [];
  const unmaterialized: CandidateRow[] = [];

  for (const sibling of siblings) {
    const existing = importedElements.get(sibling.id);
    if (existing) {
      changes.push({
        type: ProjectChangeType.ElementRemove,
        elementId: existing.id,
      });
    }
    if (discardedRows[sibling.id]) {
      delete discardedRows[sibling.id];
      discardedChanged = true;
    }

    const result = tryMaterializeCandidateRow(sibling, {
      mappingsByCode: ctx.mappingsByCode,
      productsById: ctx.productsById,
      recipesById: ctx.recipesById,
      importsById: ctx.importsById,
      draftMappingsByRowId: ctx.draftMappingsByRowId,
      ifcMeasureByRowId,
      trigger: 'auto',
    });
    if (result.ok) {
      changes.push({
        type: ProjectChangeType.ElementAdd,
        elementId: ctx.version.id,
        factory: result.element,
        generateUniqueName: false,
      });
      materialized.push(sibling);
    } else {
      unmaterialized.push(sibling);
    }
  }

  // A materialized sibling's mapping lives on the element's origin — its draft
  // is cleared in the same batch (one undo frame; the old clear-on-mount effect
  // is gone). Only siblings that fall back to INCOMPLETE keep the refill draft.
  const refillChanged = applySiblingDraftPatch(
    draftsByRowId,
    unmaterialized,
    ctx.siblingDraft,
  );
  let clearChanged = false;
  for (const sibling of materialized) {
    const existing = draftsByRowId[sibling.id];
    if (existing === undefined) continue;
    if (existing.ifcMeasure) {
      const cleaned: ImportRowDraft = { ifcMeasure: existing.ifcMeasure };
      if (isSameDraft(existing, cleaned)) continue;
      draftsByRowId[sibling.id] = cleaned;
    } else {
      delete draftsByRowId[sibling.id];
    }
    clearChanged = true;
  }
  const draftsChanged = refillChanged || clearChanged;

  const patch: Partial<VersionImports> = {};
  if (discardedChanged) patch.discardedRows = discardedRows;
  if (draftsChanged) patch.draftsByRowId = draftsByRowId;
  if (Object.keys(patch).length > 0) {
    changes.push(buildVersionImportsChange(ctx.version, patch));
  }

  return changes;
};

/**
 * Build the changes that clobber and AUTO-DISCARD every same-code sibling when a
 * discard mapping is saved with "also map matching rows" ticked (DEV-3361):
 * removes each live element, overwrites any prior discard record, and re-discards
 * the row with `type: 'auto'`. When the discard mapping also carries a product
 * the row is materialized and stashed so Restore is lossless; otherwise it is
 * discarded with no stash (Restore → INCOMPLETE).
 */
export const buildSameCodeDiscardChanges = (
  siblings: readonly CandidateRow[],
  ctx: SameCodeRecreateContext,
): ProjectChange[] => {
  if (siblings.length === 0) return [];

  const importedElements = collectImportedElementsInView(ctx.version);
  const discardedRows = { ...(ctx.version.imports?.discardedRows ?? {}) };
  const draftsByRowId = { ...(ctx.version.imports?.draftsByRowId ?? {}) };
  const ifcMeasureByRowId = buildIfcMeasureByRowId(ctx.version, siblings);
  const changes: ProjectChange[] = [];

  for (const sibling of siblings) {
    const existing = importedElements.get(sibling.id);
    if (existing) {
      changes.push({
        type: ProjectChangeType.ElementRemove,
        elementId: existing.id,
      });
    }

    const result = tryMaterializeCandidateRow(sibling, {
      mappingsByCode: ctx.mappingsByCode,
      productsById: ctx.productsById,
      recipesById: ctx.recipesById,
      importsById: ctx.importsById,
      draftMappingsByRowId: ctx.draftMappingsByRowId,
      ifcMeasureByRowId,
      trigger: 'auto',
    });
    discardedRows[sibling.id] = {
      type: 'auto',
      stashed: result.ok
        ? { element: result.element, parentId: ctx.version.id }
        : undefined,
    };
  }

  const draftsChanged = applySiblingDraftPatch(
    draftsByRowId,
    siblings,
    ctx.siblingDraft,
  );

  const patch: Partial<VersionImports> = { discardedRows };
  if (draftsChanged) patch.draftsByRowId = draftsByRowId;
  changes.push(buildVersionImportsChange(ctx.version, patch));
  return changes;
};
